import {
  Flex,
  IconButton,
  Box,
  Text,
  Badge,
  SystemStyleObject,
} from '@chakra-ui/react';
import { mdiClose } from '@mdi/js';
import {
  chakraComponents,
  components,
  GroupBase,
  OptionProps,
  Select as ReactSelect,
} from 'chakra-react-select';
import { ResourceByProductType } from 'features/context-ids/types/types';
import { ImageWithSkeleton } from '../../ImageWithSkeleton';
import { PortalIcon } from '../../PortalIcon';

type Item = {
  label: string;
  value: string;
  productCode: string;
  environment: string;
  showHeader: boolean;
};

const chakraStyles = {
  menuList: (provided: SystemStyleObject) => ({
    ...provided,
    p: 0,
  }),
  option: (provided: SystemStyleObject, state: any) => ({
    ...provided,
    borderBottom:
      state.data?.environment && !state.data?.showHeader ? '1px solid' : 'none',
    borderColor: 'gray.200',
    bg: state.isSelected ? 'gray.200' : state.isFocused ? '#f7fafc' : 'white',
    color: state.isSelected ? '#000000' : '#000000',
    _hover: {
      bg: state.isSelected ? 'gray.200' : '#f7fafc',
    },
  }),
};

export const SelectedProductRow = ({
  productCode,
  name,
  iconUrl,
  handleTenantSelect,
  removeProductHandler,
  tenants,
  index,
}: {
  productCode: string;
  name: string;
  iconUrl: string;
  handleTenantSelect: (productCode: string, tenantIdWithEnv: string) => void;
  removeProductHandler: (index: number) => void;
  tenants: ResourceByProductType[];
  index: number;
}) => (
  <Flex
    key={productCode}
    justifyContent='space-between'
    mb={4}
    data-testid={`selected-product-${productCode}`}
  >
    <Flex
      gap={4}
      minW='40'
      direction='column'
      justifyContent='start'
      alignItems='start'
    >
      {index === 0 && (
        <Text variant='subtle' fontWeight='semibold'>
          Resource
        </Text>
      )}
      <Flex alignItems='center' gap={2} mt={1}>
        <ImageWithSkeleton
          key={name}
          width='7'
          height='7'
          src={iconUrl}
          alt={name}
          flexShrink={0}
          data-testid={`product-icon-${productCode}`}
        />
        <Text data-testid={`product-name-${productCode}`}>{name}</Text>
      </Flex>
    </Flex>
    <Flex
      gap={4}
      flex={1}
      px={8}
      direction='column'
      alignItems='start'
      justifyContent='start'
    >
      {index === 0 && (
        <Text variant='subtle' fontWeight='semibold'>
          Environment
        </Text>
      )}
      <Box w='full'>
        <ReactSelect
          aria-label='Select environment'
          maxMenuHeight={274}
          components={{ ...chakraComponents, Option: CustomOption }}
          chakraStyles={chakraStyles}
          placeholder='Select'
          options={tenants.map(
            ({
              tenantName,
              tenantId,
              productCode,
              environment,
              showHeader,
            }: ResourceByProductType) => ({
              label: tenantName,
              // tenantId is not unique for live - preview. Need to construct a unique value
              value: `${tenantId} ${environment ?? 'non-env'}`,
              productCode,
              environment,
              showHeader,
            }),
          )}
          onChange={(data) => {
            const item = data as Item;
            if (item) handleTenantSelect(productCode, item.value);
          }}
          data-testid={`tenant-select-${productCode}`}
        />
      </Box>
    </Flex>
    <IconButton
      alignSelf='end'
      mb={1}
      variant='ghost'
      size='sm'
      aria-label='remove product'
      icon={<PortalIcon path={mdiClose} />}
      onClick={() => removeProductHandler(index)}
      data-testid={`remove-product-${index}`}
    />
  </Flex>
);

const getOptionBoxStyles = (isSelected: boolean) => ({
  bg: isSelected ? 'gray.200' : 'white',
  color: isSelected ? '#000000' : 'inherit',
  px: 4,
  py: 2,
  cursor: 'pointer' as const,
  _hover: {
    bg: isSelected ? 'gray.200' : '#f7fafc',
  },
});

export const CustomOption = (
  props: JSX.IntrinsicAttributes &
    OptionProps<unknown, boolean, GroupBase<unknown>>,
) => {
  const item = props.data as Item;
  const isSelected = props.isSelected;

  if (item.environment) {
    const [projectName, environmentName] = item.label.split(' | ');
    return (
      <>
        {item.showHeader && (
          <Text
            px={4}
            py={2}
            pt={4}
            textTransform='uppercase'
            variant='subtle'
            fontWeight='bold'
          >
            {projectName}
          </Text>
        )}
        <Box {...getOptionBoxStyles(isSelected)} {...props.innerProps}>
          <Flex justifyContent='space-between' alignItems='center'>
            <Text>{environmentName}</Text>
            <Badge
              textTransform='capitalize'
              colorScheme={
                item.environment === 'Preview' ? 'neutral' : 'success'
              }
            >
              {item.environment}
            </Badge>
          </Flex>
        </Box>
      </>
    );
  }

  return (
    <Box {...getOptionBoxStyles(isSelected)} {...props.innerProps}>
      <span>{item.label}</span>
    </Box>
  );
};
